/**
 * QueryClient global untuk @tanstack/vue-query.
 *
 * staleTime baseline:
 *   - default 30 detik (data list)
 *   - composable master override ke 1 jam
 *
 * Refetch behavior dimatikan untuk window focus karena admin panel banyak
 * berpindah-pindah window/tab dan refetch otomatis menambah beban server.
 *
 * cacheTime (default 2 jam): data idle yang tidak di-mount selama 2 jam
 * akan di-garbage-collect.
 *
 * Persistence:
 *   Cache di-serialize ke localStorage agar setelah hard-reload, data master
 *   dari sesi sebelumnya langsung available tanpa perlu hit BE. Hanya data
 *   yang sukses ter-fetch yang di-persist; query error dilewati (filtering
 *   di dehydrateOptions). maxAge = 12 jam — setelah itu cache di-discard
 *   total saat restore (sesuai TTL master 1 jam — restore boleh expire,
 *   query akan refetch karena sudah stale).
 */
import { QueryClient } from "@tanstack/vue-query";
import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 detik
      cacheTime: 2 * 60 * 60 * 1000, // 2 jam
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1, // retry sekali pada error transient
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: 0,
    },
  },
});

/* ============================================================
 * Persistence ke localStorage
 * ============================================================ */

const PERSIST_KEY = "jaskipin-query-cache";
const PERSIST_MAX_AGE = 12 * 60 * 60 * 1000; // 12 jam

let persister = null;
if (typeof window !== "undefined" && window.localStorage) {
  try {
    persister = createSyncStoragePersister({
      storage: window.localStorage,
      key: PERSIST_KEY,
      /* Throttle write supaya tidak mengganggu main thread tiap mutasi cache. */
      throttleTime: 1000,
    });

    persistQueryClient({
      queryClient,
      persister,
      maxAge: PERSIST_MAX_AGE,
      /* Hanya persist query yang sukses (skip error, skip loading). */
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => query.state.status === "success",
      },
    });
  } catch (err) {
    /* Quota exceeded / disabled storage / private mode → silent fallback */
    // eslint-disable-next-line no-console
    console.warn("[queryClient] persistence disabled:", err && err.message);
  }
}

/**
 * Helper untuk wipe cache yang di-persist (mis. saat logout).
 * Komponen logout sebaiknya panggil:
 *   queryClient.clear();
 *   clearPersistedCache();
 */
export function clearPersistedCache() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(PERSIST_KEY);
    }
  } catch (_) {
    /* ignore */
  }
}

export default queryClient;
